import { useEffect, useRef, useState } from "react";
import type { FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { allCategories } from "@/utils/comics";

const navLinks = [
  { label: "首页", to: "/" },
  { label: "最新更新", to: "/updates" },
  { label: "关于我们", to: "/about" },
];

export default function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [keyword, setKeyword] = useState("");
  const [categoryOpen, setCategoryOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setMobileOpen(false);
    setCategoryOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setCategoryOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const submitSearch = (event: FormEvent) => {
    event.preventDefault();
    const word = keyword.trim();
    navigate(word ? `/search?q=${encodeURIComponent(word)}` : "/search");
  };

  return (
    <header className="sticky top-0 z-50 border-b border-background-200 bg-background-50/95 backdrop-blur">
      <div className="flex h-16 w-full items-center gap-4 px-4 md:h-[68px] md:px-6 lg:px-8">
        <Link to="/" className="flex shrink-0 items-center gap-2.5">
          <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-500 text-background-50">
            <i className="ri-book-open-line text-lg"></i>
          </span>
          <span className="leading-tight">
            <span className="block font-heading text-lg font-extrabold tracking-tight text-foreground-950">
              JMComic
            </span>
            <span className="hidden text-[10px] font-medium uppercase tracking-[0.2em] text-foreground-500 sm:block">
              Comic Channel
            </span>
          </span>
        </Link>

        <nav className="hidden items-center gap-1 lg:flex">
          {navLinks.slice(0, 1).map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className={`whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                location.pathname === link.to
                  ? "bg-primary-100 text-primary-800"
                  : "text-foreground-700 hover:bg-background-100 hover:text-foreground-950"
              }`}
            >
              {link.label}
            </Link>
          ))}

          <div className="relative" ref={dropdownRef}>
            <button
              type="button"
              onClick={() => setCategoryOpen((open) => !open)}
              className={`flex items-center gap-1 whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                location.pathname.startsWith("/category")
                  ? "bg-primary-100 text-primary-800"
                  : "text-foreground-700 hover:bg-background-100 hover:text-foreground-950"
              }`}
              aria-expanded={categoryOpen}
            >
              漫画分类
              <i className={`ri-arrow-down-s-line text-base transition-transform ${categoryOpen ? "rotate-180" : ""}`}></i>
            </button>
            {categoryOpen && (
              <div className="animate-fade absolute left-0 top-full z-50 mt-2 w-[420px] rounded-lg border border-background-200 bg-background-50 p-3">
                <div className="grid grid-cols-2 gap-1.5">
                  <Link
                    to="/category/all"
                    className="col-span-2 flex items-center justify-between rounded-md bg-primary-50 px-3 py-2 text-sm font-semibold text-primary-800 hover:bg-primary-100"
                  >
                    全部漫画
                    <i className="ri-arrow-right-line"></i>
                  </Link>
                  {allCategories.map((category) => (
                    <Link
                      key={category.id}
                      to={`/category/${category.id}`}
                      className="flex items-center gap-2 rounded-md px-3 py-2 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950"
                    >
                      <span className="flex h-6 w-6 items-center justify-center rounded bg-secondary-100 text-secondary-700">
                        <i className={`${category.icon} text-sm`}></i>
                      </span>
                      {category.name}
                      <span className="ml-auto text-[11px] text-foreground-400">{category.subtitle}</span>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          {navLinks.slice(1).map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className={`whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                location.pathname === link.to
                  ? "bg-primary-100 text-primary-800"
                  : "text-foreground-700 hover:bg-background-100 hover:text-foreground-950"
              }`}
            >
              {link.label}
            </Link>
          ))}
        </nav>

        <form onSubmit={submitSearch} className="ml-auto hidden w-[300px] items-center lg:flex">
          <div className="relative w-full">
            <i className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-base text-foreground-400"></i>
            <input
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              type="search"
              placeholder="搜索漫画、作者或题材"
              className="h-10 w-full rounded-md border border-background-200 bg-background-100 pl-9 pr-20 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:bg-background-50"
              aria-label="搜索漫画"
            />
            <button
              type="submit"
              className="absolute right-1 top-1 h-8 whitespace-nowrap rounded bg-primary-500 px-3 text-xs font-semibold text-background-50 transition-colors hover:bg-primary-600"
            >
              搜索
            </button>
          </div>
        </form>

        <div className="ml-auto flex items-center gap-2 lg:ml-0">
          <Link
            to="/updates"
            className="hidden items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-3 py-2 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700 xl:flex"
          >
            <i className="ri-flashlight-line text-base text-primary-500"></i>
            今日更新
          </Link>
          <button
            type="button"
            onClick={() => setMobileOpen((open) => !open)}
            aria-label="打开菜单"
            className="flex h-10 w-10 items-center justify-center rounded-md border border-background-200 text-foreground-800 lg:hidden"
          >
            <i className={`${mobileOpen ? "ri-close-line" : "ri-menu-line"} text-xl`}></i>
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="animate-fade border-t border-background-200 bg-background-50 px-4 pb-4 pt-3 lg:hidden">
          <form onSubmit={submitSearch} className="mb-3 flex items-center gap-2">
            <div className="relative flex-1">
              <i className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-base text-foreground-400"></i>
              <input
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                type="search"
                placeholder="搜索漫画或作者"
                className="h-11 w-full rounded-md border border-background-200 bg-background-100 pl-9 pr-3 text-sm outline-none focus:border-primary-400"
                aria-label="搜索漫画"
              />
            </div>
            <button
              type="submit"
              className="h-11 whitespace-nowrap rounded-md bg-primary-500 px-4 text-sm font-semibold text-background-50"
            >
              搜索
            </button>
          </form>
          <div className="grid grid-cols-2 gap-2">
            {navLinks.slice(0, 1).map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="rounded-md bg-background-100 px-3 py-2.5 text-sm font-medium text-foreground-800"
              >
                {link.label}
              </Link>
            ))}
            <Link
              to="/category/all"
              className="rounded-md bg-background-100 px-3 py-2.5 text-sm font-medium text-foreground-800"
            >
              全部漫画
            </Link>
            {navLinks.slice(1).map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="rounded-md bg-background-100 px-3 py-2.5 text-sm font-medium text-foreground-800"
              >
                {link.label}
              </Link>
            ))}
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {allCategories.map((category) => (
              <Link
                key={category.id}
                to={`/category/${category.id}`}
                className="flex items-center gap-1.5 rounded-full bg-primary-50 px-3 py-1.5 text-xs font-medium text-primary-800"
              >
                <i className={category.icon}></i>
                {category.name}
              </Link>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}